import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import GUI from 'lil-gui'
import bundledPointsData from '../static/data/globe-points-2026-10-07.json'
import { loadSheetPoints } from './sheetData.js'

/**
 * Config
 */
// Google Sheet "Publish to web" CSV link; empty uses the bundled JSON only.
const SHEET_CSV_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vTrWkWk2NEqLG0f-En_R4cTdWetsRz7VGQvDHOPY1rQiZsWOzAv7lqb-p3lDGsCpA/pub?gid=2048957895&single=true&output=csv'

const globePointsData = await loadSheetPoints(SHEET_CSV_URL, bundledPointsData)

const STYLE_URL = 'https://tiles.openfreemap.org/styles/fiord'
const FONT_REGULAR = ['Noto Sans Regular']
const FONT_ITALIC = ['Noto Sans Italic']
const MAX_ZOOM = 12

const DEFAULT_CENTER = [-90, 7]
// Globe radius as a fraction of the viewport height: default view / farthest zoom-out.
const DEFAULT_GLOBE_FRACTION = 0.35
const MIN_GLOBE_FRACTION = 0.24

const SPACE_COLOR = '#000011'

// Navy ocean, slate-blue land (overrides on top of the OpenFreeMap "fiord" style).
const MAP_COLORS = {
    land: '#2c3654',
    water: '#14203f',
    waterway: '#1b2849',
    residential: 'hsl(228, 22%, 27%)',
    park: 'hsl(222, 22%, 30%)',
    ice: 'hsl(225, 25%, 42%)',
    oceanName: 'rgba(160, 188, 232, 0.8)',
    oceanHalo: 'rgba(12, 22, 52, 0.85)'
}

// Cluster tree (see buildClusterNode). Zooms are MapLibre zoom levels.
const CLUSTER_FAR_GRID_DEG = 36
const CLUSTER_SMALL_MAX = 3
const CLUSTER_SEPARATION_PX = 40
const CLUSTER_ROOT_MIN_OPEN_ZOOM = 2.1
const CLUSTER_CHILD_MIN_STEP = 0.4
const CLUSTER_ALL_OPEN_ZOOM = 8
const CLUSTER_NEARBY_KM = 25
const EARTH_RADIUS_KM = 6371
// Points at the same site (rounded to this many decimals) fan out in a ring around it on screen.
const SITE_KEY_DECIMALS = 3
const SITE_FAN_PX = 14
// Centre-to-centre spacing between neighbouring fanned dots.
const SITE_FAN_GAP_PX = 24

/**
 * Debug
 */
const gui = new GUI({ width: 300 })
gui.hide()
window.addEventListener('keydown', (e) =>
{
    if (e.target.matches('input, textarea, select')) return
    const toggleDebug =
        e.key === 'h' || e.key === 'H' || e.key === 'g' || e.key === 'G'
    if (toggleDebug)
        gui.show(gui._hidden)
})
const debugObject = {
    autoRotate: true,
    autoRotateSpeed: 0.05,
    showSST: false,
    sstOpacity: 0.3,
    oceanNames: true,
    placeLabels: true,
    terrain: true
}

const isMobile = () => window.matchMedia('(max-width: 768px)').matches
const mapEl = document.getElementById('map')
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
const DEG = Math.PI / 180

/** Zoom at which the whole globe's radius is `fraction` of the map height. */
function globeFitZoom(fraction, lat = DEFAULT_CENTER[1])
{
    const radiusPx = fraction * mapEl.clientHeight
    return Math.log2(radiusPx * 2 * Math.PI * Math.cos(lat * DEG) / 512)
}

/**
 * Map
 */
// MapLibre's default sky fills space with white; its atmosphere only glows on
// the sun-lit side, so the even rim glow is #globe-halo instead.
const SKY = {
    'sky-color': SPACE_COLOR,
    'horizon-color': SPACE_COLOR,
    'fog-color': SPACE_COLOR,
    'atmosphere-blend': 0
}
const PROJECTION = { type: 'globe' }

// A dark placeholder style until the basemap style arrives (no white flash).
const map = new maplibregl.Map({
    container: mapEl,
    style: { version: 8, sources: {}, layers: [], sky: SKY, projection: PROJECTION },
    center: DEFAULT_CENTER,
    zoom: globeFitZoom(DEFAULT_GLOBE_FRACTION),
    minZoom: globeFitZoom(MIN_GLOBE_FRACTION),
    maxZoom: MAX_ZOOM,
    maxPitch: 0,
    attributionControl: false,
    dragRotate: false,
    pitchWithRotate: false,
    doubleClickZoom: false,
    scrollZoom: false
})
map.touchZoomRotate.disableRotation()
map.keyboard.disableRotation()
map.setStyle(STYLE_URL, { transformStyle: (_, next) => ({ ...next, sky: SKY, projection: PROJECTION }) })

window.addEventListener('resize', () => map.setMinZoom(globeFitZoom(MIN_GLOBE_FRACTION)))

/**
 * Entity-type colours for dots, cluster badges (dominant type), the popup
 * badge and the legend. Unknown types fall back to the record's own `color`,
 * then white.
 */
const ENTITY_TYPE_COLORS = {
    'Musical Piece': '#3DDBE1',
    'Legislation': '#FCDB32',
    'Event': '#2E6417',
    'Publication': '#FF9A13',
    'Whale discovery': '#1800AD',
    'Project / archive': '#7A004B',
    'Art': '#CF0202',
    'Ceremony': '#6B1FAD',
    'Person': '#00687A',
    'Place': '#FFF3C2',
    'Other': '#9AA5B8'
}
const ENTITY_FALLBACK_COLOR = '#f9fbff'

// Display names (keys are the raw `type` values from the data); legend order.
const ENTITY_TYPE_LABELS = {
    'Musical Piece': 'Musical Piece',
    'Publication': 'Publication',
    'Whale discovery': 'Whalish Discovery',
    'Project / archive': 'Project/Archive',
    'Art': 'Art',
    'Legislation': 'Legislation',
    'Event': 'Event',
    'Ceremony': 'Ceremony',
    'Person': 'Person',
    'Place': 'Place',
    'Other': 'Other'
}
const ENTITY_TYPE_ORDER = Object.keys(ENTITY_TYPE_LABELS)

function entityLabel(type)
{
    return ENTITY_TYPE_LABELS[type] || type || ''
}

function entityColor(point)
{
    return ENTITY_TYPE_COLORS[point?.type] || point?.color || ENTITY_FALLBACK_COLOR
}

/** Colour of the most common type among `points` (ties: first seen). */
function dominantEntityColor(points)
{
    const counts = new Map()
    let best = points[0]
    let bestCount = 0
    for (const p of points)
    {
        const n = (counts.get(p.type) || 0) + 1
        counts.set(p.type, n)
        if (n > bestCount)
        {
            best = p
            bestCount = n
        }
    }
    return entityColor(best)
}

/**
 * Sphere helpers (unit vectors)
 */
function toVec(lat, lng)
{
    const phi = lat * DEG
    const lam = lng * DEG
    return [Math.cos(phi) * Math.cos(lam), Math.cos(phi) * Math.sin(lam), Math.sin(phi)]
}

function fromVec([x, y, z])
{
    return { lat: Math.asin(clamp(z, -1, 1)) / DEG, lng: Math.atan2(y, x) / DEG }
}

const dot3 = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
const angleBetween = (a, b) => Math.acos(clamp(dot3(a, b), -1, 1))

/** Count-weighted centroid of sites, as a unit vector. */
function centroidVec(sites)
{
    const v = [0, 0, 0]
    for (const s of sites)
        for (let i = 0; i < 3; i++) v[i] += s.vec[i] * s.points.length
    const len = Math.hypot(...v)
    return len > 1e-12 ? v.map((c) => c / len) : sites[0].vec
}

/**
 * Cluster tree — far-view grid buckets are the roots; each node is split into
 * two near-equal halves along its widest axis, and nodes of
 * CLUSTER_SMALL_MAX points or fewer (or a single site) break straight into
 * individual points. Every node gets an `expandZoom`: zooms ≥ it show the
 * node's children instead of the node. expandZoom strictly increases down the
 * tree, so each click (or zoom step) roughly halves the cluster.
 */
const points = globePointsData.filter((p) => !p.isGlobal && p.latitude != null && p.longitude != null)
const clusterNodes = []
const clusterRoots = []

function bisectSites(sites)
{
    const c = fromVec(centroidVec(sites))
    const cosLat = Math.cos(c.lat * DEG)
    const coords = sites.map((s) =>
    {
        const dLng = ((s.lng - c.lng + 540) % 360) - 180
        return { s, x: dLng * cosLat, y: s.lat - c.lat, w: s.points.length }
    })
    const total = coords.reduce((n, q) => n + q.w, 0)
    const variance = (key) =>
    {
        const mean = coords.reduce((sum, q) => sum + q[key] * q.w, 0) / total
        return coords.reduce((sum, q) => sum + q.w * (q[key] - mean) ** 2, 0)
    }
    const axis = variance('x') >= variance('y') ? 'x' : 'y'
    coords.sort((a, b) => a[axis] - b[axis])

    let mid = 1
    let acc = coords[0].w
    while (mid < coords.length - 1 && acc + coords[mid].w <= total / 2)
        acc += coords[mid++].w
    return [coords.slice(0, mid).map((q) => q.s), coords.slice(mid).map((q) => q.s)]
}

/** Single-link groups of sites within CLUSTER_NEARBY_KM, shaped like sites for bisectSites. */
function groupNearbySites(sites)
{
    const maxAngle = CLUSTER_NEARBY_KM / EARTH_RADIUS_KM
    const groupOf = sites.map((_, i) => i)
    const find = (i) => (groupOf[i] === i ? i : (groupOf[i] = find(groupOf[i])))
    for (let i = 0; i < sites.length; i++)
        for (let j = i + 1; j < sites.length; j++)
            if (angleBetween(sites[i].vec, sites[j].vec) < maxAngle) groupOf[find(i)] = find(j)

    const byRoot = new Map()
    sites.forEach((s, i) =>
    {
        const root = find(i)
        if (!byRoot.has(root)) byRoot.set(root, [])
        byRoot.get(root).push(s)
    })
    return [...byRoot.values()].map((members) =>
    {
        const vec = centroidVec(members)
        const { lat, lng } = fromVec(vec)
        return { sites: members, vec, lat, lng, points: members.flatMap((s) => s.points) }
    })
}

function minPairwiseAngle(vecs)
{
    let min = Infinity
    for (let i = 0; i < vecs.length; i++)
        for (let j = i + 1; j < vecs.length; j++)
            min = Math.min(min, angleBetween(vecs[i], vecs[j]))
    return min
}

function buildClusterNode(sites, parent)
{
    const nodePoints = sites.flatMap((s) => s.points)
    const vec = centroidVec(sites)
    const center = fromVec(vec)
    const node = {
        id: clusterNodes.length,
        points: nodePoints,
        count: nodePoints.length,
        vec,
        lngLat: [center.lng, center.lat],
        color: dominantEntityColor(nodePoints),
        parent,
        children: null,
        expandZoom: 0
    }
    clusterNodes.push(node)

    let halves = null
    if (sites.length > 1 && nodePoints.length > CLUSTER_SMALL_MAX)
    {
        // Split between groups of nearby sites first, so close neighbours stay together.
        const groups = groupNearbySites(sites)
        halves = groups.length > 1
            ? bisectSites(groups).map((half) => half.flatMap((g) => g.sites))
            : bisectSites(sites)
    }

    // Zoom at which the node's parts sit CLUSTER_SEPARATION_PX apart on
    // screen, but at least CLUSTER_CHILD_MIN_STEP past its parent and no
    // later than CLUSTER_ALL_OPEN_ZOOM.
    let raw = -Infinity
    const parts = halves ? halves.map(centroidVec) : sites.length > 1 ? sites.map((s) => s.vec) : null
    if (parts)
    {
        const theta = minPairwiseAngle(parts)
        raw = theta > 1e-9
            ? Math.log2(CLUSTER_SEPARATION_PX * 2 * Math.PI * Math.cos(center.lat * DEG) / (theta * 512))
            : Infinity
    }
    const lo = parent ? parent.expandZoom + CLUSTER_CHILD_MIN_STEP : CLUSTER_ROOT_MIN_OPEN_ZOOM
    node.expandZoom = clamp(raw, Math.min(lo, CLUSTER_ALL_OPEN_ZOOM), CLUSTER_ALL_OPEN_ZOOM)

    if (halves)
        node.children = halves.map((half) => buildClusterNode(half, node))
    else
        for (const p of nodePoints) p._leaf = node

    return node
}

function buildSites(pts)
{
    const byKey = new Map()
    for (const p of pts)
    {
        const key = `${p.latitude.toFixed(SITE_KEY_DECIMALS)},${p.longitude.toFixed(SITE_KEY_DECIMALS)}`
        if (!byKey.has(key))
            byKey.set(key, { lat: p.latitude, lng: p.longitude, vec: toVec(p.latitude, p.longitude), points: [] })
        byKey.get(key).points.push(p)
    }
    for (const site of byKey.values())
        site.points.forEach((p, k) => { p._site = site; p._fanIndex = k })
    return [...byKey.values()]
}

function buildClusterTree()
{
    const buckets = new Map()
    for (const site of buildSites(points))
    {
        const key = `${Math.floor((site.lat + 90) / CLUSTER_FAR_GRID_DEG)}/${Math.floor((site.lng + 180) / CLUSTER_FAR_GRID_DEG)}`
        if (!buckets.has(key)) buckets.set(key, [])
        buckets.get(key).push(site)
    }
    for (const sites of buckets.values())
        clusterRoots.push(buildClusterNode(sites, null))
}

points.forEach((p, i) => { p._index = i })
buildClusterTree()

if (points.length === 0)
    console.warn('No placeable markers: check globe JSON (latitude/longitude vs isGlobal).')

/**
 * Where a point is drawn at `zoom`: its own position, or — when it shares a
 * site with others — a fixed SITE_FAN_PX ring around the site.
 */
function displayLngLat(p, zoom)
{
    const site = p._site
    const n = site.points.length
    if (n < 2) return [p.longitude, p.latitude]
    const radiusPx = Math.max(SITE_FAN_PX, SITE_FAN_GAP_PX / (2 * Math.sin(Math.PI / n)))
    const angle = (2 * Math.PI * p._fanIndex) / n
    const degPerPx = 360 / (512 * 2 ** zoom)
    return [
        site.lng + Math.sin(angle) * radiusPx * degPerPx,
        site.lat + Math.cos(angle) * radiusPx * degPerPx * Math.cos(site.lat * DEG)
    ]
}

/** Clusters and individual points shown at `zoom`. */
function visibleItems(zoom)
{
    const clusters = []
    const dots = []
    const walk = (node) =>
    {
        if (zoom < node.expandZoom)
        {
            if (node.count === 1) dots.push(node.points[0])
            else clusters.push(node)
        }
        else if (node.children) node.children.forEach(walk)
        else dots.push(...node.points)
    }
    clusterRoots.forEach(walk)
    return { clusters, dots }
}

/** On the hemisphere facing the viewer (with a small margin before the horizon). */
function isFacing(vec)
{
    const c = map.getCenter()
    return dot3(vec, toVec(c.lat, c.lng)) > 0.1
}

/** Legend of the entity types present on the globe, in ENTITY_TYPE_ORDER (unlisted types last). */
function renderTypeLegend(pts)
{
    const legend = document.getElementById('type-legend')
    if (!legend) return

    const present = new Set(pts.map((p) => p.type).filter(Boolean))
    const rank = (type) =>
    {
        const i = ENTITY_TYPE_ORDER.indexOf(type)
        return i === -1 ? Infinity : i
    }

    legend.replaceChildren()
    for (const type of [...present].sort((a, b) => rank(a) - rank(b)))
    {
        const item = document.createElement('li')
        item.className = 'type-legend__item'
        const swatch = document.createElement('span')
        swatch.className = 'type-legend__swatch'
        swatch.style.background = swatch.style.color = entityColor({ type })
        item.append(swatch, document.createTextNode(entityLabel(type)))
        legend.append(item)
    }
}

renderTypeLegend(points)

/**
 * Ocean / sea / gulf names. The style's own water names are sparse at globe
 * zooms, so a curated list fills in: oceans always, seas from zoom 2.1, gulfs
 * from 2.7. Past zoom 6 the style's water labels take over.
 */
const oceanFeatures = [
    // --- Oceans ---
    { name: 'Pacific Ocean',      lat:  -5,  lng: -160, type: 'ocean' },
    { name: 'North Pacific Ocean',lat:  30,  lng: -160, type: 'ocean' },
    { name: 'South Pacific Ocean',lat: -35,  lng: -125, type: 'ocean' },
    { name: 'Atlantic Ocean',     lat:  10,  lng:  -40, type: 'ocean' },
    { name: 'North Atlantic',     lat:  40,  lng:  -40, type: 'ocean' },
    { name: 'South Atlantic',     lat: -25,  lng:  -15, type: 'ocean' },
    { name: 'Indian Ocean',       lat: -15,  lng:   80, type: 'ocean' },
    { name: 'Arctic Ocean',       lat:  85,  lng:    0, type: 'ocean' },
    { name: 'Southern Ocean',     lat: -65,  lng:   30, type: 'ocean' },
    // --- Seas ---
    { name: 'Mediterranean Sea',  lat:  36,  lng:   18, type: 'sea' },
    { name: 'Caribbean Sea',      lat:  15,  lng:  -75, type: 'sea' },
    { name: 'North Sea',          lat:  56,  lng:    4, type: 'sea' },
    { name: 'Norwegian Sea',      lat:  68,  lng:    3, type: 'sea' },
    { name: 'Bering Sea',         lat:  58,  lng: -175, type: 'sea' },
    { name: 'Sea of Japan',       lat:  40,  lng:  135, type: 'sea' },
    { name: 'East China Sea',     lat:  29,  lng:  125, type: 'sea' },
    { name: 'South China Sea',    lat:  13,  lng:  115, type: 'sea' },
    { name: 'Philippine Sea',     lat:  18,  lng:  133, type: 'sea' },
    { name: 'Bay of Bengal',      lat:  15,  lng:   89, type: 'sea' },
    { name: 'Arabian Sea',        lat:  15,  lng:   65, type: 'sea' },
    { name: 'Red Sea',            lat:  22,  lng:   38, type: 'sea' },
    { name: 'Black Sea',          lat:  43,  lng:   35, type: 'sea' },
    { name: 'Coral Sea',          lat: -18,  lng:  155, type: 'sea' },
    { name: 'Tasman Sea',         lat: -40,  lng:  160, type: 'sea' },
    { name: 'Labrador Sea',       lat:  60,  lng:  -55, type: 'sea' },
    { name: 'Greenland Sea',      lat:  75,  lng:   -5, type: 'sea' },
    { name: 'Baltic Sea',         lat:  58,  lng:   20, type: 'sea' },
    // --- Gulfs / Bays ---
    { name: 'Gulf of Mexico',     lat:  25,  lng:  -90, type: 'gulf' },
    { name: 'Hudson Bay',         lat:  60,  lng:  -85, type: 'gulf' },
    { name: 'Persian Gulf',       lat:  27,  lng:   51, type: 'gulf' },
    { name: 'Gulf of Alaska',     lat:  57,  lng: -145, type: 'gulf' },
    { name: 'Gulf of Guinea',     lat:   2,  lng:    3, type: 'gulf' },
    { name: 'Sea of Okhotsk',     lat:  54,  lng:  150, type: 'gulf' }
]

const OCEAN_NAMES_MAX_ZOOM = 6
const STYLE_WATER_NAMES_MIN_ZOOM = 5

/**
 * SST overlay (NASA GIBS, MUR 0.25°). The 5-day lag keeps the requested day
 * inside GIBS' processing window.
 */
function isoDateNDaysAgo(n)
{
    const d = new Date()
    d.setUTCDate(d.getUTCDate() - n)
    return d.toISOString().slice(0, 10)
}
const SST_TILES = `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/GHRSST_L4_MUR25_Sea_Surface_Temperature/default/${isoDateNDaysAgo(5)}/GoogleMapsCompatible_Level6/{z}/{y}/{x}.png`

/**
 * Style + layers
 */
const MARKER_LAYERS = ['markers-dot']
// Land label tiers, zooming in: continents → countries, with the data points'
// own places (see addDataPlaceLabels) → all major cities at once → cities
// around the data → nearby towns.
//   nearKm:         only within that distance of a data point...
//   everywhereFrom: ...until this zoom, after which it shows everywhere.
const MAJOR_CITY_ZOOM = 3
const SURROUNDING_LABEL_RADIUS_KM = 150
const LOCAL_LABEL_RADIUS_KM = 40
const COUNTRY_TIER_ZOOM = 2.2
const DATA_PLACE_LABEL_ZOOM = [2.5, 6]
const LABEL_TIERS = {
    place_continent: { maxzoom: 2.6 },
    place_country_major: { minzoom: COUNTRY_TIER_ZOOM, maxzoom: 7 },
    place_country_minor: { minzoom: 3.8, maxzoom: 7 },
    place_country_other: { minzoom: 3.8, maxzoom: 7 },
    place_city_large: { minzoom: MAJOR_CITY_ZOOM },
    place_city: { minzoom: 4.5, nearKm: SURROUNDING_LABEL_RADIUS_KM, everywhereFrom: 6.5 },
    place_town: { minzoom: 6, nearKm: LOCAL_LABEL_RADIUS_KM },
    highway_name_other: { nearKm: LOCAL_LABEL_RADIUS_KM },
    highway_ref: { nearKm: LOCAL_LABEL_RADIUS_KM }
}
const HIDDEN_LABEL_LAYERS = ['place_state', 'place_village', 'place_suburb', 'place_other']
// Extra space kept clear around city labels, so fewer show at once.
const CITY_LABEL_PADDING_PX = 12
const ENGLISH_NAME = ['coalesce', ['get', 'name:en'], ['get', 'name_en'], ['get', 'name']]
let placeLabelLayers = []

function setPaintIfLayer(id, prop, value)
{
    if (map.getLayer(id)) map.setPaintProperty(id, prop, value)
}

function recolorBasemap()
{
    setPaintIfLayer('background', 'background-color', MAP_COLORS.land)
    setPaintIfLayer('water', 'fill-color', MAP_COLORS.water)
    setPaintIfLayer('waterway', 'line-color', MAP_COLORS.waterway)
    setPaintIfLayer('landuse_residential', 'fill-color', MAP_COLORS.residential)
    // Parks include huge marine reserves, which read as odd blobs on the ocean.
    for (const id of ['park', 'park_outline'])
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none')
    setPaintIfLayer('landcover_ice_shelf', 'fill-color', MAP_COLORS.ice)
    setPaintIfLayer('road_area_pier', 'fill-color', MAP_COLORS.land)
    setPaintIfLayer('road_pier', 'line-color', MAP_COLORS.land)
    setPaintIfLayer('boundary_country_z0-4', 'line-opacity', 0.45)
    setPaintIfLayer('boundary_country_z5-', 'line-opacity', 0.55)
    // Drop maritime borders (the outlines offset around coasts and islands).
    for (const id of ['boundary_state', 'boundary_country_z0-4', 'boundary_country_z5-'])
    {
        if (!map.getLayer(id)) continue
        map.setFilter(id, ['all', map.getFilter(id), ['!=', ['get', 'maritime'], 1]])
    }
    if (map.getLayer('water_name'))
        map.setLayerZoomRange('water_name', STYLE_WATER_NAMES_MIN_ZOOM, 24)

    const symbolLayers = map.getStyle().layers.filter((l) => l.type === 'symbol')
    for (const { id } of symbolLayers)
    {
        layFlatOnGlobe(id)
        if (id !== 'highway_ref') map.setLayoutProperty(id, 'text-field', ENGLISH_NAME)
    }

    for (const id of HIDDEN_LABEL_LAYERS)
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none')

    const nearAreas = new Map()
    for (const [id, tier] of Object.entries(LABEL_TIERS))
    {
        const layer = map.getLayer(id)
        if (!layer) continue
        map.setLayerZoomRange(id, tier.minzoom ?? layer.minzoom ?? 0, tier.maxzoom ?? layer.maxzoom ?? 24)
        // Zoom may only drive a filter through a top-level step, so each
        // branch repeats the style's own filter.
        const base = map.getFilter(id)
        if (tier.nearKm)
        {
            if (!nearAreas.has(tier.nearKm)) nearAreas.set(tier.nearKm, nearDataArea(tier.nearKm))
            const near = ['all', base, ['within', nearAreas.get(tier.nearKm)]]
            map.setFilter(id, tier.everywhereFrom ? ['step', ['zoom'], near, tier.everywhereFrom, base] : near)
        }
        if (id.startsWith('place_city')) map.setLayoutProperty(id, 'text-padding', CITY_LABEL_PADDING_PX)
    }

    placeLabelLayers = symbolLayers
        .map((l) => l.id)
        .filter((id) => id.startsWith('place_') && !HIDDEN_LABEL_LAYERS.includes(id))
}

/** Circles of `radiusKm` around every data site, as a MultiPolygon. */
function nearDataArea(radiusKm)
{
    const sites = [...new Set(points.map((p) => p._site))]
    const steps = 48
    const polygons = sites.map((site) =>
    {
        const dLat = radiusKm / 111.32
        const dLng = dLat / Math.max(Math.cos(site.lat * DEG), 0.05)
        const ring = []
        for (let i = 0; i <= steps; i++)
        {
            const a = (2 * Math.PI * i) / steps
            ring.push([site.lng + Math.sin(a) * dLng, site.lat + Math.cos(a) * dLat])
        }
        return [ring]
    })
    return { type: 'MultiPolygon', coordinates: polygons }
}

/** Text drawn on the globe's surface (curving with it) instead of facing the screen. */
function layFlatOnGlobe(id)
{
    map.setLayoutProperty(id, 'text-pitch-alignment', 'map')
    map.setLayoutProperty(id, 'text-rotation-alignment', 'map')
}

function firstSymbolLayerId()
{
    return map.getStyle().layers.find((l) => l.type === 'symbol')?.id
}

/**
 * Terrain + bathymetry from AWS Terrain Tiles (Terrarium-encoded elevation,
 * keyless): an elevation colour ramp with hillshade, under the vector water
 * so coastlines stay crisp while ocean depth shows through.
 */
const TERRAIN_TILES = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'
const TERRAIN_LAYERS = ['terrain-relief', 'terrain-hillshade']
const TERRAIN_COLORS = [
    -8000, '#081430',
    -5000, '#0d1d42',
    -3000, '#13285a',
    -1000, '#1b3770',
    -200, '#25498a',
    -20, '#30599c',
    0, '#35485a',
    300, '#3e5650',
    1000, '#56664f',
    2000, '#71705a',
    3500, '#8c8577',
    5000, '#c9cbd4'
]

function addTerrainLayers()
{
    map.addSource('terrain-dem', {
        type: 'raster-dem',
        tiles: [TERRAIN_TILES],
        encoding: 'terrarium',
        tileSize: 256,
        maxzoom: 12,
        attribution: 'Terrain Tiles: Mapzen / AWS'
    })
    const before = map.getLayer('water') ? 'water' : firstSymbolLayerId()
    const visibility = debugObject.terrain ? 'visible' : 'none'
    map.addLayer({
        id: 'terrain-relief',
        type: 'color-relief',
        source: 'terrain-dem',
        layout: { visibility },
        paint: {
            'color-relief-color': ['interpolate', ['linear'], ['elevation'], ...TERRAIN_COLORS]
        }
    }, before)
    map.addLayer({
        id: 'terrain-hillshade',
        type: 'hillshade',
        source: 'terrain-dem',
        layout: { visibility },
        paint: {
            'hillshade-exaggeration': 0.45,
            'hillshade-shadow-color': '#050b1e',
            'hillshade-highlight-color': 'rgba(220, 230, 255, 0.35)',
            'hillshade-accent-color': '#0a1530'
        }
    }, before)
    applyTerrainWater()
}

/** With terrain on, the vector water becomes a light navy veil over the bathymetry. */
function applyTerrainWater()
{
    setPaintIfLayer('water', 'fill-opacity', debugObject.terrain ? 0.35 : 1)
}

function addSSTLayer()
{
    map.addSource('sst', {
        type: 'raster',
        tiles: [SST_TILES],
        tileSize: 256,
        maxzoom: 6,
        attribution: 'NASA GIBS / JPL MUR'
    })
    map.addLayer({
        id: 'sst',
        type: 'raster',
        source: 'sst',
        layout: { visibility: debugObject.showSST ? 'visible' : 'none' },
        paint: { 'raster-opacity': debugObject.sstOpacity, 'raster-fade-duration': 200 }
    }, firstSymbolLayerId())
}

function addOceanNamesLayer()
{
    map.addSource('ocean-names', {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: oceanFeatures.map((f) => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [f.lng, f.lat] },
                properties: { name: f.name, kind: f.type }
            }))
        }
    })
    const byKind = (ocean, sea, gulf) => ['match', ['get', 'kind'], 'ocean', ocean, 'sea', sea, gulf]
    map.addLayer({
        id: 'ocean-names',
        type: 'symbol',
        source: 'ocean-names',
        maxzoom: OCEAN_NAMES_MAX_ZOOM,
        filter: ['step', ['zoom'],
            ['==', ['get', 'kind'], 'ocean'],
            2.1, ['in', ['get', 'kind'], ['literal', ['ocean', 'sea']]],
            2.7, true
        ],
        layout: {
            'text-field': ['get', 'name'],
            'text-font': FONT_ITALIC,
            'text-size': byKind(13, 11, 10),
            'text-letter-spacing': byKind(0.25, 0.15, 0.12),
            'text-transform': 'uppercase',
            'text-max-width': 8,
            'text-pitch-alignment': 'map',
            'text-rotation-alignment': 'map',
            visibility: debugObject.oceanNames ? 'visible' : 'none'
        },
        paint: {
            'text-color': MAP_COLORS.oceanName,
            'text-halo-color': MAP_COLORS.oceanHalo,
            'text-halo-width': 1.2
        }
    }, firstSymbolLayerId())
}

/**
 * The data points' own places (their `location`), shown from the country tier
 * — before the basemap's own small-city labels exist — until nearby basemap
 * labels take over. Records whose country is "International" (oceans, seas)
 * are skipped. Added above the basemap labels so they win label collisions.
 */
function addDataPlaceLabels()
{
    const byName = new Map()
    for (const p of points)
    {
        if (!p.location || p.country === 'International') continue
        if (!byName.has(p.location)) byName.set(p.location, [])
        byName.get(p.location).push(p)
    }
    const features = [...byName].map(([name, pts]) => ({
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: [
                pts.reduce((s, p) => s + p.longitude, 0) / pts.length,
                pts.reduce((s, p) => s + p.latitude, 0) / pts.length
            ]
        },
        properties: { name }
    }))

    map.addSource('data-places', { type: 'geojson', data: { type: 'FeatureCollection', features } })
    map.addLayer({
        id: 'data-places',
        type: 'symbol',
        source: 'data-places',
        minzoom: DATA_PLACE_LABEL_ZOOM[0],
        maxzoom: DATA_PLACE_LABEL_ZOOM[1],
        layout: {
            'text-field': ['get', 'name'],
            'text-font': FONT_REGULAR,
            'text-size': 12,
            'text-anchor': 'top',
            'text-offset': [0, 1.1],
            'text-max-width': 8,
            'text-pitch-alignment': 'map',
            'text-rotation-alignment': 'map',
            visibility: debugObject.placeLabels ? 'visible' : 'none'
        },
        paint: {
            'text-color': 'hsl(195, 30%, 85%)',
            'text-halo-color': MAP_COLORS.oceanHalo,
            'text-halo-width': 1.2
        }
    })
    placeLabelLayers.push('data-places')
}

// Marker radii (px). Zoom may only drive a paint value through a top-level
// interpolate, so the dot sizes are spelled out per zoom stop.
const CLUSTER_RADIUS = ['interpolate', ['linear'], ['sqrt', ['get', 'count']], 1.41, 8, 3.74, 12]
const IS_ACTIVE = ['to-boolean', ['get', 'active']]
const dotRadius = (normal, active) => ['case', IS_ACTIVE, active, normal]
const MARKER_GLOWS = [
    { id: 'markers-glow-outer', scale: 2.2, blur: 1, opacity: 0.3 },
    { id: 'markers-glow-inner', scale: 1.4, blur: 0.6, opacity: 0.45 }
]
// Two rings per marker breathing in opposite phase: a thicker inner ring and a
// thin outer one. Radius and width are multiples of the marker radius; the
// pulse factor (0.75–1.05) lives in feature-state so animating it doesn't
// re-tile the source every frame.
const MARKER_RINGS = [
    { id: 'markers-ring-inner', radius: 1.6, width: 0.4, phaseOffset: 0 },
    { id: 'markers-ring-outer', radius: 2.2, width: 0.2, phaseOffset: Math.PI }
]
const RING_OPACITY = 0.3
const RING_PULSE_SPEED = 1.2

function markerRadius(scale = 1)
{
    const at = (dotNormal, dotActive) => ['case', ['==', ['get', 'kind'], 'cluster'],
        ['*', CLUSTER_RADIUS, scale],
        dotRadius(['*', dotNormal, scale], ['*', dotActive, scale])]
    return ['interpolate', ['linear'], ['zoom'], 2, at(6, 8.5), 8, at(8, 11)]
}

const ringPulse = (index) => ['coalesce', ['feature-state', `pulse${index}`], 1]

function addMarkerLayers()
{
    map.addSource('markers', { type: 'geojson', data: markerFeatures() })

    // Wide soft halo plus a tighter bright one under every marker.
    for (const glow of MARKER_GLOWS)
    {
        map.addLayer({
            id: glow.id,
            type: 'circle',
            source: 'markers',
            paint: {
                'circle-color': ['get', 'color'],
                'circle-radius': markerRadius(glow.scale),
                'circle-blur': glow.blur,
                'circle-opacity': ['case', IS_ACTIVE, glow.opacity + 0.3, glow.opacity]
            }
        })
    }
    MARKER_RINGS.forEach((ring, i) =>
    {
        map.addLayer({
            id: ring.id,
            type: 'circle',
            source: 'markers',
            paint: {
                'circle-color': 'rgba(0, 0, 0, 0)',
                'circle-radius': markerRadius(['*', ring.radius, ringPulse(i)]),
                'circle-stroke-color': ['get', 'color'],
                'circle-stroke-width': markerRadius(['*', ring.width, ringPulse(i)]),
                'circle-stroke-opacity': RING_OPACITY
            }
        })
    })
    map.addLayer({
        id: 'markers-dot',
        type: 'circle',
        source: 'markers',
        paint: {
            'circle-color': ['get', 'color'],
            'circle-radius': markerRadius()
        }
    })
}

/**
 * Marker data — rebuilt whenever the zoom (cluster tier / site fan) or the
 * selection changes.
 */
let activePoint = null

function markerFeatures()
{
    const zoom = map.getZoom()
    const { clusters, dots } = visibleItems(zoom)
    const features = []
    for (const node of clusters)
    {
        features.push({
            type: 'Feature',
            id: node.id * 2 + 1,
            geometry: { type: 'Point', coordinates: node.lngLat },
            properties: { kind: 'cluster', id: node.id, count: node.count, color: node.color }
        })
    }
    for (const p of dots)
    {
        features.push({
            type: 'Feature',
            id: p._index * 2,
            geometry: { type: 'Point', coordinates: displayLngLat(p, zoom) },
            properties: { kind: 'dot', id: p._index, color: entityColor(p), active: p === activePoint }
        })
    }
    markerFeatureIds = features.map((f) => f.id)
    return { type: 'FeatureCollection', features }
}

/**
 * Ring pulse — each marker gets a stable pseudo-random phase from its feature
 * id so neighbours don't breathe in sync.
 */
let markerFeatureIds = []
const markerPhase = (featureId) => (featureId * 2.399963) % (Math.PI * 2)

function animateRings(now)
{
    requestAnimationFrame(animateRings)
    if (!basemapReady || document.hidden) return
    const t = now / 1000 * RING_PULSE_SPEED
    for (const id of markerFeatureIds)
    {
        const state = {}
        MARKER_RINGS.forEach((ring, i) =>
        {
            state[`pulse${i}`] = 0.9 + Math.sin(t + markerPhase(id) + ring.phaseOffset) * 0.15
        })
        map.setFeatureState({ source: 'markers', id }, state)
    }
}
requestAnimationFrame(animateRings)

let basemapReady = false
let markersDirty = false
function refreshMarkers()
{
    if (markersDirty) return
    markersDirty = true
    requestAnimationFrame(() =>
    {
        markersDirty = false
        map.getSource('markers')?.setData(markerFeatures())
    })
}

map.on('style.load', () =>
{
    if (!map.getSource('openmaptiles')) return
    recolorBasemap()
    addTerrainLayers()
    addSSTLayer()
    addOceanNamesLayer()
    addDataPlaceLabels()
    addMarkerLayers()
    basemapReady = true
    updateHalo()
})

/**
 * Atmosphere halo — a radial glow behind the globe's silhouette (with no
 * pitch the globe centre is always at the screen centre). Fades out as the
 * globe fills the screen.
 */
const HALO_FADE_ZOOM = [3.5, 5]
const haloEl = document.getElementById('globe-halo')

function updateHalo()
{
    const zoom = map.getZoom()
    const t = clamp((zoom - HALO_FADE_ZOOM[0]) / (HALO_FADE_ZOOM[1] - HALO_FADE_ZOOM[0]), 0, 1)
    const opacity = basemapReady ? 1 - t * t * (3 - 2 * t) : 0
    haloEl.style.opacity = opacity
    if (opacity === 0) return

    const fov = map.transform.fov * DEG
    const camToCenter = 0.5 * mapEl.clientHeight / Math.tan(fov / 2)
    const globeRadius = 512 * 2 ** zoom / (2 * Math.PI * Math.cos(map.getCenter().lat * DEG))
    const silhouette = camToCenter * Math.tan(Math.asin(globeRadius / (camToCenter + globeRadius)))
    haloEl.style.setProperty('--globe-radius', `${silhouette}px`)
    const centre = map.project(map.getCenter())
    haloEl.style.left = `${centre.x}px`
    haloEl.style.top = `${centre.y}px`
}

map.on('zoom', refreshMarkers)
map.on('zoom', updateZoomHud)
map.on('move', updateHalo)
map.on('resize', updateHalo)
map.on('move', updatePopupPosition)

/**
 * Sidebar — the text column collapses to just the toggle and legend. The globe
 * stays centred in the space right of whatever is showing.
 */
const sidebarEl = document.getElementById('sidebar')
const sidebarToggle = document.getElementById('sidebar-toggle')

function sidebarPadding()
{
    const left = isMobile() ? 0 : Math.round(sidebarEl.getBoundingClientRect().right)
    return { left, right: 0, top: 0, bottom: 0 }
}

function setSidebarCollapsed(collapsed, animate = true)
{
    sidebarEl.classList.toggle('sidebar--collapsed', collapsed)
    sidebarToggle.setAttribute('aria-expanded', String(!collapsed))
    sidebarToggle.setAttribute('aria-label', collapsed ? 'Show text' : 'Hide text')
    if (animate) map.easeTo({ padding: sidebarPadding(), duration: 500 })
    else map.setPadding(sidebarPadding())
}

sidebarToggle.addEventListener('click', () =>
    setSidebarCollapsed(!sidebarEl.classList.contains('sidebar--collapsed')))
setSidebarCollapsed(isMobile(), false)
window.addEventListener('resize', () => map.setPadding(sidebarPadding()))

/**
 * GUI
 */
const mapFolder = gui.addFolder('Map')
mapFolder.add(debugObject, 'terrain').name('Terrain / Ocean Depth').onChange((v) =>
{
    for (const id of TERRAIN_LAYERS)
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', v ? 'visible' : 'none')
    applyTerrainWater()
})
mapFolder.add(debugObject, 'showSST').name('Show SST').onChange((v) =>
{
    if (map.getLayer('sst')) map.setLayoutProperty('sst', 'visibility', v ? 'visible' : 'none')
})
mapFolder.add(debugObject, 'sstOpacity').min(0).max(1).step(0.01).name('SST Opacity').onChange((v) =>
{
    setPaintIfLayer('sst', 'raster-opacity', v)
})
mapFolder.add(debugObject, 'oceanNames').name('Ocean / Sea Names').onChange((v) =>
{
    if (map.getLayer('ocean-names')) map.setLayoutProperty('ocean-names', 'visibility', v ? 'visible' : 'none')
})
mapFolder.add(debugObject, 'placeLabels').name('Place Labels').onChange((v) =>
{
    for (const id of placeLabelLayers) map.setLayoutProperty(id, 'visibility', v ? 'visible' : 'none')
})

const controlsFolder = gui.addFolder('Controls')
controlsFolder.add(debugObject, 'autoRotate').name('Auto Rotate')
controlsFolder.add(debugObject, 'autoRotateSpeed').min(0).max(2).step(0.05).name('Rotate Speed')

/**
 * Popup
 */
const popup = document.getElementById('popup')
const popupClose = document.getElementById('popup-close')
const popupTitle = document.getElementById('popup-title')
const popupType = document.getElementById('popup-type')
const popupDate = document.getElementById('popup-date')
const popupLocation = document.getElementById('popup-location')
const popupText = document.getElementById('popup-text')
const popupPeople = document.getElementById('popup-people')
const popupMedia = document.getElementById('popup-media')
const popupEmbed = document.getElementById('popup-embed')
const popupEmbedFrame = document.getElementById('popup-embed-frame')
const popupEmbedCredit = document.getElementById('popup-embed-credit')

/**
 * Classify a media URL for inline embedding. Only YouTube and direct images
 * are embedded — most other sites refuse to be framed, so they stay a link.
 */
const IMAGE_PATH_RE = /\.(jpe?g|png|gif|webp|avif)$/i

function parseMediaUrl(raw)
{
    let url
    try { url = new URL(raw) } catch { return null }

    const host = url.hostname.replace(/^(www\.|m\.)/, '')
    if (host === 'youtube.com' || host === 'youtu.be')
    {
        const list = url.searchParams.get('list')
        const id = host === 'youtu.be' ? url.pathname.slice(1) : url.searchParams.get('v')
        const params = new URLSearchParams({ rel: '0' })
        if (list) params.set('list', list)
        if (id) return { kind: 'video', src: `https://www.youtube-nocookie.com/embed/${id}?${params}` }
        if (list) return { kind: 'video', src: `https://www.youtube-nocookie.com/embed/videoseries?${params}` }
    }

    if (IMAGE_PATH_RE.test(url.pathname)) return { kind: 'image', src: url.href }
    return null
}

function clearPopupEmbed()
{
    // Removing the iframe also stops any playing video.
    popupEmbedFrame.replaceChildren()
    popupEmbed.classList.add('hidden-link')
    popupEmbed.classList.remove('popup-embed--video')
}

function renderPopupMedia(data)
{
    clearPopupEmbed()
    popupMedia.classList.add('hidden-link')

    const showLink = (label, href = data.media) =>
    {
        popupMedia.href = href
        popupMedia.textContent = label
        popupMedia.classList.remove('hidden-link')
    }

    // The record's page (Link) gets the button; media that can't be embedded
    // falls back to it when there's no page.
    if (data.link) showLink('Read more ↗', data.link)
    if (!data.media) return

    const media = parseMediaUrl(data.media)
    if (!media)
    {
        if (!data.link) showLink('Read more ↗')
        return
    }

    if (media.kind === 'video')
    {
        const iframe = document.createElement('iframe')
        iframe.src = media.src
        iframe.title = data.title || 'Video'
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
        iframe.allowFullscreen = true
        iframe.loading = 'lazy'
        iframe.referrerPolicy = 'strict-origin-when-cross-origin'
        popupEmbedFrame.append(iframe)
        popupEmbed.classList.add('popup-embed--video')
    }
    else
    {
        const img = document.createElement('img')
        img.src = media.src
        img.alt = data.title || ''
        img.referrerPolicy = 'no-referrer'
        // Hotlink-protected or dead images fall back to a plain link.
        img.addEventListener('error', () =>
        {
            clearPopupEmbed()
            if (!data.link) showLink('View media ↗')
        })
        popupEmbedFrame.append(img)
    }

    popupEmbedCredit.textContent = data.mediaCredit || ''
    popupEmbed.classList.remove('hidden-link')
}

function showPopup(data)
{
    popupTitle.textContent = data.title

    popupType.textContent = entityLabel(data.type)
    const bgColor = entityColor(data)
    popupType.style.background = bgColor + '40'
    popupType.style.color = bgColor

    popupDate.textContent = data.date || ''

    const locationParts = [data.location, data.country].filter(Boolean)
    popupLocation.textContent = locationParts.join(', ')

    popupText.textContent = data.text || ''
    popupPeople.textContent = data.keyPeople || ''

    renderPopupMedia(data)

    popup.classList.remove('hidden')
    popup.scrollTop = 0
}

function selectPoint(p)
{
    activePoint = p
    showPopup(p)
    refreshMarkers()
    updatePopupPosition()
}

function hidePopup()
{
    popup.classList.add('hidden')
    clearPopupEmbed()
    if (activePoint)
    {
        activePoint = null
        refreshMarkers()
    }
}

/**
 * Keep the popup beside the active point while the map moves.
 */
const POPUP_OFFSET_X = 20
const POPUP_OFFSET_Y = -20

function updatePopupPosition()
{
    if (!activePoint || popup.classList.contains('hidden')) return

    // Hide when the point rotates behind the globe.
    if (!isFacing(activePoint._site.vec))
    {
        hidePopup()
        return
    }

    if (isMobile())
    {
        popup.style.left = '50%'
        popup.style.top = '50%'
        popup.style.transform = 'translate(-50%, -50%)'
        return
    }

    const mapRect = mapEl.getBoundingClientRect()
    const projected = map.project(displayLngLat(activePoint, map.getZoom()))
    const screenX = mapRect.left + projected.x
    const screenY = mapRect.top + projected.y
    const popupRect = popup.getBoundingClientRect()
    let left = screenX + POPUP_OFFSET_X
    let top = screenY + POPUP_OFFSET_Y

    if (left + popupRect.width > mapRect.right - 10)
        left = Math.max(screenX - popupRect.width - POPUP_OFFSET_X, mapRect.left + map.getPadding().left + 10)
    if (top + popupRect.height > mapRect.bottom - 10)
        top = mapRect.bottom - popupRect.height - 10
    if (top < mapRect.top + 10)
        top = mapRect.top + 10

    popup.style.left = `${left}px`
    popup.style.top = `${top}px`
    popup.style.transform = 'none'
}

popupClose.addEventListener('click', (e) =>
{
    e.stopPropagation()
    hidePopup()
})

/**
 * Fly-to
 */
const FLY_MIN_DURATION = 450
const FLY_MAX_DURATION = 1600
const FLY_OPEN_MARGIN = 0.25
// Single points fly in to this zoom unless already at least this close.
const FLY_SINGLE_END_ZOOM = 5
const SINGLE_CLOSE_VIEW_TOLERANCE = 0.3
// Where a selected single point lands on screen (fractions of the map area),
// leaving room for the popup.
const POPUP_MARKER_ANCHOR_DESKTOP = { x: 0.4, y: 0.39 }
const POPUP_MARKER_ANCHOR_MOBILE = { x: 0.5, y: 0.12 }
const FLY_EASING = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

// Camera animations are tagged with an id so their own moveend (fired on
// completion or when a gesture/new animation interrupts them) runs onDone.
let animationId = 0
let pendingAnimation = null

map.on('moveend', (e) =>
{
    if (!pendingAnimation || e.animationId !== pendingAnimation.id) return
    const { onDone } = pendingAnimation
    pendingAnimation = null
    if (onDone) onDone()
})

function animateCamera(method, options, onDone = null)
{
    const id = ++animationId
    pendingAnimation = { id, onDone }
    zoomStepActive = false
    map[method]({ ...options, essential: true, easing: FLY_EASING }, { animationId: id })
}

function flyDuration(lngLat, zoom)
{
    const c = map.getCenter()
    const angle = angleBetween(toVec(c.lat, c.lng), toVec(lngLat[1], lngLat[0])) / DEG
    return clamp(FLY_MIN_DURATION + angle * 15 + Math.abs(zoom - map.getZoom()) * 120, FLY_MIN_DURATION, FLY_MAX_DURATION)
}

/** Fly so `lngLat` sits `offset` px from the screen centre, then call onDone. */
function flyTo(lngLat, zoom, { offset = [0, 0], onDone = null } = {}, duration = flyDuration(lngLat, zoom))
{
    zoom = clamp(zoom, map.getMinZoom(), map.getMaxZoom())
    animateCamera('flyTo', { center: lngLat, zoom, offset, duration }, onDone)
}

function cancelFly()
{
    pendingAnimation = null
    zoomStepActive = false
    map.stop()
}

function screenAnchorOffset()
{
    const a = isMobile() ? POPUP_MARKER_ANCHOR_MOBILE : POPUP_MARKER_ANCHOR_DESKTOP
    const visibleWidth = mapEl.clientWidth - map.getPadding().left
    return [(a.x - 0.5) * visibleWidth, (a.y - 0.5) * mapEl.clientHeight]
}

/** Fly a single point to the popup anchor, then select it if the flight wasn't interrupted. */
function flyToPoint(p, zoom)
{
    zoom = clamp(zoom, map.getMinZoom(), map.getMaxZoom())
    const target = displayLngLat(p, zoom)
    const offset = screenAnchorOffset()
    flyTo(target, zoom, {
        offset,
        onDone: () =>
        {
            const at = map.project(displayLngLat(p, map.getZoom()))
            const centre = map.project(map.getCenter())
            const want = [centre.x + offset[0], centre.y + offset[1]]
            if (Math.hypot(at.x - want[0], at.y - want[1]) < 40) selectPoint(p)
        }
    })
}

/**
 * Clicks and hover
 */
map.on('click', (e) =>
{
    const feature = map.queryRenderedFeatures(e.point, { layers: MARKER_LAYERS })[0]
    hidePopup()
    if (!feature) return

    const { kind, id } = feature.properties
    if (kind === 'cluster')
    {
        const node = clusterNodes[id]
        flyTo(node.lngLat, node.expandZoom + FLY_OPEN_MARGIN)
        return
    }

    // Far away: fly in to the close view. Already close: keep the zoom and
    // just glide the point to the popup anchor.
    const p = points[id]
    const zoom = map.getZoom()
    const closeView = zoom >= FLY_SINGLE_END_ZOOM - SINGLE_CLOSE_VIEW_TOLERANCE
    flyToPoint(p, closeView ? zoom : FLY_SINGLE_END_ZOOM)
})

for (const layer of MARKER_LAYERS)
{
    map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = '' })
}

/**
 * Stepped zoom (scroll wheel + zoom buttons). Each step flies to the next
 * cluster level among what's on screen — a split when zooming in, a merge when
 * zooming out. Levels within ZOOM_LEVEL_GROUP of that one count as the same
 * stop. It lands FLY_OPEN_MARGIN past the group, or halfway to the following
 * level if that's closer, so the next stop is never passed over. With no level
 * left in that direction, it steps by ZOOM_FREE_STEP.
 */
const ZOOM_FREE_STEP = 0.75
const ZOOM_LEVEL_GROUP = 0.3
const ZOOM_STEP_DURATION = 650
const WHEEL_STEP_THRESHOLD = 60
const WHEEL_GESTURE_GAP_MS = 220

function zoomLevelTarget(direction)
{
    const zoom = map.getZoom()
    const { clusters, dots } = visibleItems(zoom)
    const levels = new Set()
    let target

    if (direction > 0)
    {
        // Split zooms of every facing cluster and its descendants.
        const addTree = (node) =>
        {
            if (node.count > 1 && node.expandZoom > zoom + 0.01) levels.add(node.expandZoom)
            if (node.children) node.children.forEach(addTree)
        }
        for (const node of clusters)
            if (isFacing(node.vec)) addTree(node)

        const sorted = [...levels].sort((a, b) => a - b)
        if (!sorted.length) target = zoom + ZOOM_FREE_STEP
        else
        {
            let last = 0
            while (last + 1 < sorted.length && sorted[last + 1] - sorted[0] < ZOOM_LEVEL_GROUP) last++
            const following = sorted[last + 1]
            target = sorted[last] + FLY_OPEN_MARGIN
            if (following !== undefined) target = Math.min(target, (sorted[last] + following) / 2)
        }
    }
    else
    {
        // Merge zooms along the ancestor chain of everything facing.
        const addChain = (node) =>
        {
            for (let n = node; n; n = n.parent)
                if (n.count > 1 && n.expandZoom < zoom - 0.01) levels.add(n.expandZoom)
        }
        for (const node of clusters)
            if (isFacing(node.vec)) addChain(node.parent)
        for (const p of dots)
            if (isFacing(p._site.vec)) addChain(p._leaf)

        const sorted = [...levels].sort((a, b) => b - a)
        if (!sorted.length) target = zoom - ZOOM_FREE_STEP
        else
        {
            let last = 0
            while (last + 1 < sorted.length && sorted[0] - sorted[last + 1] < ZOOM_LEVEL_GROUP) last++
            const following = sorted[last + 1]
            target = sorted[last] - FLY_OPEN_MARGIN
            if (following !== undefined) target = Math.max(target, (sorted[last] + following) / 2)
        }
    }

    return clamp(target, map.getMinZoom(), map.getMaxZoom())
}

let zoomStepActive = false

function stepZoom(direction)
{
    if (zoomStepActive) return
    const target = zoomLevelTarget(direction)
    if (Math.abs(target - map.getZoom()) < 1e-3) return
    animateCamera('easeTo', { zoom: target, duration: ZOOM_STEP_DURATION }, () => { zoomStepActive = false })
    zoomStepActive = true
}

function zoomIn() { stepZoom(1) }
function zoomOut() { stepZoom(-1) }

// Scroll steps through levels instead of zooming continuously (pinch-zoom on
// touch is untouched). One step per scroll gesture: after a step, input is
// ignored until the wheel goes quiet for WHEEL_GESTURE_GAP_MS, so trackpad
// momentum (which keeps firing after the step lands) can't trigger another.
let wheelAccum = 0
let wheelLastTime = 0
let wheelGestureUsed = false
map.getCanvasContainer().addEventListener('wheel', (e) =>
{
    e.preventDefault()

    const now = performance.now()
    if (now - wheelLastTime > WHEEL_GESTURE_GAP_MS)
    {
        wheelAccum = 0
        wheelGestureUsed = false
    }
    wheelLastTime = now
    if (zoomStepActive || wheelGestureUsed) return

    wheelAccum += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY
    if (Math.abs(wheelAccum) >= WHEEL_STEP_THRESHOLD)
    {
        stepZoom(wheelAccum < 0 ? 1 : -1)
        wheelAccum = 0
        wheelGestureUsed = true
    }
}, { passive: false })

/**
 * Reset
 */
const RESET_DURATION = 800

function startReset()
{
    hidePopup()
    cancelFly()
    zoomStepActive = false
    flyTo(DEFAULT_CENTER, globeFitZoom(DEFAULT_GLOBE_FRACTION), {}, RESET_DURATION)
}

/**
 * Auto-rotate — spins the globe eastward while idle. Pauses while the mouse
 * is over the map (mouse only, so taps on touch devices don't stop it), while
 * a popup is open, and during any camera movement.
 */
const AUTO_ROTATE_DEG_PER_SEC_PER_SPEED = 12
let isHoveringMap = false
const mapContainer = map.getContainer()
mapContainer.addEventListener('mouseenter', () => { isHoveringMap = true })
mapContainer.addEventListener('mouseleave', () => { isHoveringMap = false })

let lastRotateTime = performance.now()
function autoRotate(now)
{
    const dt = Math.min((now - lastRotateTime) / 1000, 0.1)
    lastRotateTime = now
    if (debugObject.autoRotate && !isHoveringMap && !activePoint && !zoomStepActive && !map.isMoving())
    {
        const c = map.getCenter()
        map.setCenter([c.lng - debugObject.autoRotateSpeed * AUTO_ROTATE_DEG_PER_SEC_PER_SPEED * dt, c.lat])
    }
    requestAnimationFrame(autoRotate)
}
map.once('load', () => requestAnimationFrame(autoRotate))

/**
 * Zoom HUD
 */
const zoomHudValue = document.getElementById('zoom-hud-value')
let lastHudZoom = NaN

function updateZoomHud()
{
    const z = Math.round(map.getZoom() * 10) / 10
    if (z === lastHudZoom) return
    lastHudZoom = z
    zoomHudValue.textContent = z.toFixed(1)
}
updateZoomHud()

/**
 * Controls
 */
document.getElementById('zoom-in').addEventListener('click', zoomIn)
document.getElementById('zoom-out').addEventListener('click', zoomOut)
document.getElementById('reset-view').addEventListener('click', startReset)
